import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { pxToRem } from '../../assets/styles/helper';
import Modal from '../Modal/Modal';
import ModalBody from '../Modal/ModalBody';
import ModalButtons from '../Modal/ModalButtons';
import MultiEmail from '../InputFields/MultiEmailField';
import { referralEmailValidationSchema } from '../../validation/referralEmailValidationSchema';
import FormContainer from '../FormContainer/FormContainer';
import themeColors from '../../assets/styles/themeColors';
import { useReferAFriendEvent } from '../../util/helpers/referralCodeHelper';
import { getCurrentAccount } from '../../store/selectors/accountSelectors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import config from '../../config';
import Anchor from '../Anchor/Anchor';

// Animation for floating money icons
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(5deg); }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Main container with gradient background
const ReferralBannerContainer = styled.div`
  background: linear-gradient(135deg, #ff6b35 0%, #ffd89b 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  margin: 16px;
  box-shadow: 0 8px 32px rgba(255, 107, 53, 0.2);
  min-height: 70px;

  @media (min-width: 1200px) {
    padding: 14px 32px;
    min-height: 65px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 24px 16px;
    gap: 16px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    margin: 8px;
    padding: 20px 12px;
  }
`;

// Content wrapper
const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  z-index: 2;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

// Text container for better alignment
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

// Title styling
const ReferralTitle = styled.h2`
  color: #ffffff;
  font-size: 22px;
  font-weight: 800;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px;
  line-height: 1.2;

  @media (min-width: 1200px) {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 22px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

// Subtitle/description
const ReferralSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  margin: 2px 0 0 0;
  opacity: 0.95;
  letter-spacing: 0.3px;

  @media (min-width: 1200px) {
    font-size: 15px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-top: 6px;
  }
`;

// Modern button styling
const ReferralButton = styled.button`
  background: linear-gradient(135deg, #ff4757 0%, #ff3742 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.4);
  z-index: 2;
  white-space: nowrap;

  @media (min-width: 1200px) {
    padding: 14px 28px;
    font-size: 14px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 71, 87, 0.6);
    animation: ${bounce} 0.6s ease-in-out;
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 13px;
  }
`;

// Decorative elements
const MoneyIcon = styled.div`
  position: absolute;
  color: rgba(255, 255, 255, 0.25);
  font-size: ${(props) => props.size || '18px'};
  animation: ${float} 3s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};
  pointer-events: none;
  z-index: 1;

  &.icon-1 {
    top: 50%;
    right: 180px;
    transform: translateY(-50%);
    font-size: 16px;
  }

  &.icon-2 {
    top: 20%;
    right: 140px;
    font-size: 14px;
  }

  &.icon-3 {
    bottom: 25%;
    right: 160px;
    font-size: 15px;
  }

  &.icon-4 {
    top: 15px;
    right: 15px;
    font-size: 16px;
  }

  &.icon-5 {
    bottom: 15px;
    right: 20px;
    font-size: 14px;
  }

  @media (max-width: 1024px) {
    &.icon-1 {
      right: 160px;
    }
    &.icon-2 {
      right: 120px;
    }
    &.icon-3 {
      right: 140px;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Decorative background pattern
const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 80%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
  z-index: 1;
`;

export const ReferralBannerTtitle = styled.p`
  color: #833940;
  font-size: ${pxToRem(14)};
  line-height: 1.45;
  font-weight: 700;
  margin-right: ${pxToRem(14)};
`;

export const ReferralBannerText = styled.p`
  color: ${themeColors.colorLabel};
  font-size: ${pxToRem(14)};
  line-height: 1.45;
  font-weight: 500;
  margin-bottom: ${pxToRem(14)};
`;

const HAA_PMC_URL =
  'https://hardassetsalliance.com/referral-program-terms-and-conditions';
const GSS_PMC_URL =
  'https://goldsilver.com/referral-program-terms-and-conditions';

const ReferralBanner = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReferredSuccessfully, setIsReferredSuccessfully] = useState(false);

  const account = useSelector(getCurrentAccount);
  const settings = useSelector(selectSettings);
  const { brokerTheme } = config;
  const isHAA = brokerTheme === 'HardAssetsAlliance';
  const broker = isHAA ? 'Hard Assets Alliance' : 'GoldSilver';

  const handleReferAFriend = useReferAFriendEvent();

  const submitForm = (values) => {
    handleReferAFriend(account, values.emails, handleOnSuccess);
  };

  const handleOnSuccess = (data) => {
    setIsReferredSuccessfully(data);
  };

  const handleModalClose = () => {
    setIsReferredSuccessfully(false);
    setIsModalOpen(false);
  };

  if (!settings?.IsReferralProgramActive) return;

  return (
    <div>
      <ReferralBannerContainer>
        <BackgroundPattern />

        {/* Decorative money icons */}
        <MoneyIcon className="icon-1" delay="0s">
          💰
        </MoneyIcon>
        <MoneyIcon className="icon-2" delay="1s">
          💵
        </MoneyIcon>
        <MoneyIcon className="icon-3" delay="2s">
          🪙
        </MoneyIcon>
        <MoneyIcon className="icon-4" delay="0.5s">
          💸
        </MoneyIcon>

        <ContentWrapper>
          <TextContainer>
            <ReferralTitle>{t('referral.title')}</ReferralTitle>
            <ReferralSubtitle>
              {t('referral.subtitle', {
                amount: settings && settings?.ReferralAmount,
                brokerName: broker || t('common.defaultBrokerName'),
              })}
            </ReferralSubtitle>
          </TextContainer>
        </ContentWrapper>

        <ReferralButton onClick={() => setIsModalOpen(true)}>
          {t('referral.clickHere')}
        </ReferralButton>
      </ReferralBannerContainer>
      <Modal
        title={t('referral.title')}
        size="md"
        isOpen={isModalOpen}
        close={handleModalClose}
      >
        <ModalBody>
          {isReferredSuccessfully ? (
            <>
              <ReferralBannerText>
                {t('referral.referralSuccessMessage')}
              </ReferralBannerText>
              <ModalButtons
                isHorizontal
                marginTop
                primaryButtonProps={{
                  label: t('common.ok'),
                  onClick: handleModalClose,
                }}
                secondaryButtonProps={{
                  onClick: handleModalClose,
                }}
              />
            </>
          ) : (
            <Formik
              initialValues={{
                emails: [],
                currentEmail: '',
              }}
              validationSchema={referralEmailValidationSchema}
              onSubmit={submitForm}
              validateOnBlur={false}
            >
              {({ values }) => (
                <Form>
                  <ReferralBannerText>
                    {t('referral.message', {
                      amount: settings && settings?.ReferralAmount,
                      brokerName: broker || t('common.defaultBrokerName'),
                    })}
                    <Anchor
                      bold
                      type="website"
                      marginBottom={32}
                      value={isHAA ? HAA_PMC_URL : GSS_PMC_URL}
                      text={t(`referral.pmc`)}
                    />
                  </ReferralBannerText>
                  <FormContainer>
                    <Field
                      name="emails"
                      label={t('referral.emailLabel')}
                      component={MultiEmail}
                    />
                    <ModalButtons
                      isHorizontal
                      marginTop
                      primaryButtonProps={{
                        label: t('common.confirm'),
                        type: 'submit',
                        disabled: values.emails.length === 0,
                      }}
                      secondaryButtonProps={{
                        onClick: handleModalClose,
                      }}
                    />
                  </FormContainer>
                </Form>
              )}
            </Formik>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default React.memo(ReferralBanner);
